import * as React from 'react';
import { useToast } from '@rocketmakers/armstrong';
import { useAuth0 } from '@auth0/auth0-react';

export type ErrorKind =
  | 'ServerError'
  | 'UnauthenticatedError'
  | 'ForbiddenError'
  | 'ValidationError'
  | 'ArgumentError'
  | 'NotFoundError';
export interface IValidationErrorMessages {
  developer: string;
  friendly?: string;
}
export interface IValidationErrorDetail {
  attribute?: string;
  path: string;
  value: any;
  messages: IValidationErrorMessages;
}

type Error = { message: string };
type NetworkError = Error & {
  networkError: {
    name: string;
    statusCode: number;
    result: { name: string; statusCode: number; isGraphQLError: boolean };
  };
};

const isNetworkError = (error: any): error is NetworkError => !!(error as NetworkError)?.networkError?.result;
const isValidationError = (error: any): error is any => !!(error as any)?.message;
const errorToastTime = 5000;
export const useApiError = (warning?: boolean) => {
  const { user, isLoading, logout } = useAuth0();
  const { dispatch } = useToast();

  return React.useCallback(
    (error: any) => {
      if (!user && !isLoading) {
        /* eslint-disable-next-line no-console */
        dispatch({
          title: 'Unauthenticated',
          message: error,
          type: 'error',
          autodismiss: errorToastTime,
        });
        logout();
      } else if (isNetworkError(error)) {
        dispatch({
          title: error.networkError.result.name,
          message: error.message,
          type: 'error',
          autodismiss: errorToastTime,
        });
      } else if (isValidationError(error) && error.developerMessage) {
        const missingFields = JSON.parse(error.developerMessage).errors as IValidationErrorDetail[];
        const missingFieldsString = missingFields.map((x) => x.attribute).join('\n');
        dispatch({
          title: 'The following fields and sections are either missing or invalid',
          message: missingFieldsString,
          type: 'warning',
          allowManualDismiss: true,
        });
      } else if (error.response?.data?.message) {
        dispatch({ title: 'error', type: 'error', message: error.response.data.message, autodismiss: errorToastTime });
      } else if (error.message) {
        dispatch({ title: 'error', type: 'error', message: error.message, autodismiss: errorToastTime });
      } else {
        dispatch({
          title: warning ? 'warning' : 'error',
          type: 'error',
          message: JSON.stringify(error),
          autodismiss: errorToastTime,
        });
      }
    },
    [dispatch],
  );
};

export const useToastOnError = (error: any) => {
  const dispatch = useApiError();

  React.useEffect(() => {
    if (error) {
      dispatch(error);
    }
  }, [error]);
};
